/**
 * IndexedDB Utility for Model Undo/Redo History Storage
 *
 * Purpose: Persistent storage for undo/redo history across browser sessions
 * Database: 'ModelHistoryDB'
 * Store: 'histories' with keyPath 'modelId'
 *
 * Storage Structure:
 * {
 *   modelId: string,           // Primary key (mdlrMdlId)
 *   history: {
 *     past: Array,             // Past states for undo
 *     future: Array            // Future states for redo
 *   },
 *   timestamp: number          // Last update timestamp
 * }
 *
 * Capacity: ~50MB+ per domain (can store ~100 snapshots per model)
 */

class HistoryDB {
  constructor() {
    this.db = null;
    this.DB_NAME = 'ModelHistoryDB';
    this.DB_VERSION = 1;
    this.STORE_NAME = 'histories';
    this.initFailed = false; // IndexedDB 초기화 실패 플래그
    this.initAttempts = 0;
    this.MAX_INIT_ATTEMPTS = 2;

    // BroadcastChannel for cross-tab synchronization
    this.channel = null;
    this.listeners = new Set();

    // Initialize BroadcastChannel if supported
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel('model-history-sync');
      } catch (error) {
        console.warn('[HistoryDB] BroadcastChannel not supported:', error);
      }
    }
  }

  /**
   * Initialize IndexedDB connection
   * @returns {Promise<IDBDatabase|null>} Database instance or null if unavailable
   */
  async init() {
    // 이미 초기화 실패한 경우 재시도하지 않음
    if (this.initFailed) {
      return null;
    }

    if (this.db) {
      return this.db;
    }

    // IndexedDB가 지원되지 않는 환경 체크
    if (typeof indexedDB === 'undefined') {
      console.warn('[HistoryDB] IndexedDB not supported in this environment');
      this.initFailed = true;
      return null;
    }

    this.initAttempts++;

    return new Promise((resolve) => {
      try {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        request.onerror = () => {
          console.error('[HistoryDB] IndexedDB initialization failed:', request.error);

          // Internal error 발생 시 DB 삭제 후 재시도
          if (this.initAttempts < this.MAX_INIT_ATTEMPTS &&
              request.error?.name === 'UnknownError') {
            this.deleteDatabase().then(() => {
              this.init().then(resolve);
            }).catch(() => {
              this.initFailed = true;
              resolve(null);
            });
          } else {
            this.initFailed = true;
            resolve(null);
          }
        };

        request.onsuccess = () => {
          this.db = request.result;
          this.initFailed = false;
          this.initAttempts = 0;
          resolve(this.db);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;

          // Create object store if it doesn't exist
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            const objectStore = db.createObjectStore(this.STORE_NAME, {
              keyPath: 'modelId'
            });

            // Create index for timestamp (useful for cleanup/debugging)
            objectStore.createIndex('timestamp', 'timestamp', { unique: false });

          }
        };

        // 타임아웃 설정 (5초)
        setTimeout(() => {
          if (!this.db && !this.initFailed) {
            console.warn('[HistoryDB] IndexedDB initialization timeout');
            this.initFailed = true;
            resolve(null);
          }
        }, 5000);
      } catch (error) {
        console.error('[HistoryDB] IndexedDB init exception:', error);
        this.initFailed = true;
        resolve(null);
      }
    });
  }

  /**
   * Delete the entire database (for recovery from corruption)
   * @returns {Promise<void>}
   */
  async deleteDatabase() {
    return new Promise((resolve, reject) => {
      // 기존 연결 닫기
      if (this.db) {
        this.db.close();
        this.db = null;
      }

      const request = indexedDB.deleteDatabase(this.DB_NAME);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('[HistoryDB] Failed to delete database:', request.error);
        reject(request.error);
      };

      request.onblocked = () => {
        console.warn('[HistoryDB] Database deletion blocked - other connections open');
        resolve(); // blocked여도 계속 진행
      };
    });
  }

  /**
   * Subscribe to history updates from other tabs
   * @param {Function} callback - Callback function called when history is updated in another tab
   * @returns {Function} Unsubscribe function
   */
  onHistoryUpdate(callback) {
    if (!this.channel) {
      console.warn('[HistoryDB] BroadcastChannel not available, cross-tab sync disabled');
      return () => {};
    }

    const listener = (event) => {
      if (event.data && event.data.type === 'history-updated') {
        callback(event.data);
      }
    };

    this.listeners.add(listener);
    this.channel.addEventListener('message', listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
      if (this.channel) {
        this.channel.removeEventListener('message', listener);
      }
    };
  }

  /**
   * Broadcast history update to other tabs
   * @param {string} modelId - Model identifier
   * @param {Object} history - Updated history object
   */
  broadcastUpdate(modelId, history) {
    if (!this.channel) return;

    try {
      this.channel.postMessage({
        type: 'history-updated',
        modelId,
        history,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('[HistoryDB] Failed to broadcast update:', error);
    }
  }

  /**
   * Save undo/redo history for a specific model
   * @param {string} modelId - Model identifier (mdlrMdlId)
   * @param {Object} history - History object with past/future arrays
   * @param {Array} history.past - Past states for undo
   * @param {Array} history.future - Future states for redo
   * @returns {Promise<void>}
   */
  async saveHistory(modelId, history) {
    if (!modelId) {
      console.warn('[HistoryDB] saveHistory: modelId is required');
      return;
    }

    if (!history || (!history.past && !history.future)) {
      console.warn('[HistoryDB] saveHistory: invalid history object');
      return;
    }

    try {
      const db = await this.init();

      // IndexedDB 사용 불가능한 경우 조용히 종료 (앱 작동에 영향 없음)
      if (!db) {
        console.warn('[HistoryDB] saveHistory: IndexedDB unavailable, history will not persist');
        return;
      }

      const data = {
        modelId,
        history: {
          past: Array.isArray(history.past) ? history.past : [],
          future: Array.isArray(history.future) ? history.future : []
        },
        timestamp: Date.now()
      };

      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.put(data);

        request.onsuccess = () => {

          // Broadcast update to other tabs
          this.broadcastUpdate(modelId, data.history);

          resolve();
        };

        request.onerror = () => {
          console.error('[HistoryDB] Failed to save history:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[HistoryDB] saveHistory error:', error);
      // 에러를 throw하지 않음 - 앱이 계속 작동하도록 함
    }
  }

  /**
   * Load undo/redo history for a specific model
   * @param {string} modelId - Model identifier (mdlrMdlId)
   * @returns {Promise<Object|null>} History object or null if not found
   */
  async loadHistory(modelId) {
    if (!modelId) {
      console.warn('[HistoryDB] loadHistory: modelId is required');
      return null;
    }

    try {
      const db = await this.init();

      // IndexedDB 사용 불가능한 경우 null 반환
      if (!db) {
        console.warn('[HistoryDB] loadHistory: IndexedDB unavailable');
        return null;
      }

      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        const request = store.get(modelId);

        request.onsuccess = () => {
          const result = request.result;

          if (result && result.history) {
            // timestamp를 history 객체에 포함하여 반환
            resolve({
              ...result.history,
              timestamp: result.timestamp
            });
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error('[HistoryDB] Failed to load history:', request.error);
          resolve(null); // reject 대신 null 반환하여 앱이 계속 작동하도록 함
        };
      });
    } catch (error) {
      console.error('[HistoryDB] loadHistory error:', error);
      return null;
    }
  }

  /**
   * Delete history for a specific model
   * @param {string} modelId - Model identifier (mdlrMdlId)
   * @returns {Promise<void>}
   */
  async deleteHistory(modelId) {
    if (!modelId) {
      console.warn('[HistoryDB] deleteHistory: modelId is required');
      return;
    }

    try {
      const db = await this.init();

      if (!db) {
        console.warn('[HistoryDB] deleteHistory: IndexedDB unavailable');
        return;
      }

      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        const request = store.delete(modelId);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('[HistoryDB] Failed to delete history:', request.error);
          resolve(); // 에러가 발생해도 계속 진행
        };
      });
    } catch (error) {
      console.error('[HistoryDB] deleteHistory error:', error);
    }
  }

  /**
   * Get all stored histories (for debugging/monitoring)
   * @returns {Promise<Array>} Array of all history records
   */
  async getAllHistories() {
    try {
      const db = await this.init();

      if (!db) {
        console.warn('[HistoryDB] getAllHistories: IndexedDB unavailable');
        return [];
      }

      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        const request = store.getAll();

        request.onsuccess = () => {
          const histories = request.result || [];
          resolve(histories);
        };

        request.onerror = () => {
          console.error('[HistoryDB] Failed to get all histories:', request.error);
          resolve([]);
        };
      });
    } catch (error) {
      console.error('[HistoryDB] getAllHistories error:', error);
      return [];
    }
  }

  /**
   * Clear all histories (for cleanup/maintenance)
   * @returns {Promise<void>}
   */
  async clearAllHistories() {
    try {
      const db = await this.init();

      if (!db) {
        console.warn('[HistoryDB] clearAllHistories: IndexedDB unavailable');
        return;
      }

      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        const request = store.clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('[HistoryDB] Failed to clear histories:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error('[HistoryDB] clearAllHistories error:', error);
    }
  }

  /**
   * Get storage usage statistics
   * @returns {Promise<Object>} Storage statistics
   */
  async getStorageStats() {
    try {
      const histories = await this.getAllHistories();

      const stats = {
        totalModels: histories.length,
        totalSnapshots: 0,
        estimatedSizeKB: 0,
        models: []
      };

      histories.forEach(record => {
        const pastCount = record.history.past?.length || 0;
        const futureCount = record.history.future?.length || 0;
        const totalCount = pastCount + futureCount;

        // Estimate size (rough approximation)
        const estimatedSize = JSON.stringify(record.history).length / 1024;

        stats.totalSnapshots += totalCount;
        stats.estimatedSizeKB += estimatedSize;

        stats.models.push({
          modelId: record.modelId,
          pastCount,
          futureCount,
          totalCount,
          estimatedSizeKB: estimatedSize.toFixed(2),
          lastUpdate: new Date(record.timestamp).toLocaleString()
        });
      });

      stats.estimatedSizeKB = stats.estimatedSizeKB.toFixed(2);

      return stats;
    } catch (error) {
      console.error('getStorageStats error:', error);
      return null;
    }
  }

  /**
   * Close database connection and cleanup resources
   */
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }

    // Close BroadcastChannel
    if (this.channel) {
      // Remove all listeners
      this.listeners.forEach(listener => {
        this.channel.removeEventListener('message', listener);
      });
      this.listeners.clear();

      // Close channel
      this.channel.close();
      this.channel = null;
    }
  }
}

// Export singleton instance
export const historyDB = new HistoryDB();

// Export class for testing
export { HistoryDB };
